import { useState } from "react";
import { format } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import { Calendar as CalendarIcon, Clock, Video, Mail, MessageCircle } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Reveal, Overline } from "@/components/site/Reveal";
import NutritionForm from "@/components/site/NutritionForm";
import { EMAIL, WA_DEFAULT_MSG, waLink } from "@/lib/data";
import { useLang } from "@/lib/i18n";

const SLOTS = ["09:00", "10:30", "12:00", "14:00", "15:30", "17:00", "18:30"];

const Contact = () => {
    const [date, setDate] = useState(null);
    const [slot, setSlot] = useState(null);
    const { tr, lang } = useLang();
    const locale = lang === "en" ? enUS : fr;

    const dateLabel = date ? format(date, "EEEE d MMMM yyyy", { locale }) : null;
    const mailHref =
        date && slot
            ? `mailto:${EMAIL}?subject=${encodeURIComponent(
                  tr(`Consultation Zoom — ${dateLabel} à ${slot}`, `Zoom consultation — ${dateLabel} at ${slot}`)
              )}&body=${encodeURIComponent(
                  tr(
                      `Bonjour Coach Roméo,\n\nJe souhaite réserver une consultation de 30 minutes (25 $) en visioconférence Zoom.\n\nDate souhaitée : ${dateLabel}\nHeure : ${slot}\n\nNom :\nTéléphone :\nObjectif principal :\n\nMerci !`,
                      `Hello Coach Roméo,\n\nI would like to book a 30-minute consultation ($25) on Zoom.\n\nPreferred date: ${dateLabel}\nTime: ${slot}\n\nName:\nPhone:\nMain goal:\n\nThank you!`
                  )
              )}`
            : null;

    return (
        <div data-testid="contact-page" className="pt-28 md:pt-36">
            <section className="pb-24 md:pb-32">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Reveal>
                        <Overline>{tr("Réservation", "Booking")}</Overline>
                        <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-3xl">
                            {tr("Réserve ta consultation", "Book your")}{" "}
                            <span className="font-accent italic font-medium text-ochre">{tr("à 25 $.", "consultation — $25.")}</span>
                        </h1>
                        <p className="mt-6 text-sand max-w-2xl leading-relaxed font-light">
                            {tr(
                                "30 minutes en visioconférence Zoom pour parler de tes objectifs et repartir avec un plan d'action clair. Choisis une date et un créneau — ta demande part directement dans ma boîte courriel, et je te confirme le lien Zoom.",
                                "30 minutes on Zoom to talk about your goals and leave with a clear action plan. Pick a date and a time — your request goes straight to my inbox, and I'll confirm the Zoom link."
                            )}
                        </p>
                    </Reveal>

                    <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
                        <Reveal delay={0.1} className="lg:col-span-7">
                            <div className="border border-white/10 bg-obsidian-surface p-6 md:p-10">
                                <p className="flex items-center gap-3 font-heading font-bold uppercase tracking-tight text-lg">
                                    <CalendarIcon className="h-5 w-5 text-terracotta" /> {tr("1 · Choisis une date", "1 · Pick a date")}
                                </p>
                                <div className="mt-6 flex justify-center" data-testid="booking-calendar">
                                    <Calendar
                                        mode="single"
                                        selected={date}
                                        onSelect={(d) => {
                                            setDate(d);
                                            setSlot(null);
                                        }}
                                        disabled={{ before: new Date() }}
                                        locale={locale}
                                        className="rounded-md border border-white/10 bg-obsidian text-cream"
                                    />
                                </div>

                                <p className="mt-10 flex items-center gap-3 font-heading font-bold uppercase tracking-tight text-lg">
                                    <Clock className="h-5 w-5 text-terracotta" /> {tr("2 · Choisis un créneau", "2 · Pick a time")}
                                </p>
                                <div className="mt-6 grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="booking-slots">
                                    {SLOTS.map((s) => (
                                        <button
                                            key={s}
                                            data-testid={`booking-slot-${s.replace(":", "")}`}
                                            onClick={() => date && setSlot(s)}
                                            disabled={!date}
                                            className={`rounded-full border py-3 text-sm font-bold tracking-wider transition-colors duration-300 ${
                                                slot === s
                                                    ? "bg-terracotta border-terracotta text-white"
                                                    : date
                                                      ? "border-white/15 text-cream hover:border-ochre hover:text-ochre"
                                                      : "border-white/5 text-sand/40 cursor-not-allowed"
                                            }`}
                                        >
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </Reveal>

                        <Reveal delay={0.2} className="lg:col-span-5">
                            <div className="border border-terracotta/40 bg-terracotta/5 p-6 md:p-10 lg:sticky lg:top-32">
                                <p className="flex items-center gap-3 font-heading font-bold uppercase tracking-tight text-lg">
                                    <Video className="h-5 w-5 text-terracotta" /> {tr("3 · Confirme", "3 · Confirm")}
                                </p>
                                <div className="mt-6 space-y-3 text-sm">
                                    <div className="flex justify-between border-b border-white/10 pb-3">
                                        <span className="text-sand">{tr("Date", "Date")}</span>
                                        <span className="font-bold text-cream" data-testid="booking-summary-date">
                                            {dateLabel || tr("À choisir", "To choose")}
                                        </span>
                                    </div>
                                    <div className="flex justify-between border-b border-white/10 pb-3">
                                        <span className="text-sand">{tr("Heure", "Time")}</span>
                                        <span className="font-bold text-cream" data-testid="booking-summary-slot">
                                            {slot || tr("À choisir", "To choose")}
                                        </span>
                                    </div>
                                    <div className="flex justify-between border-b border-white/10 pb-3">
                                        <span className="text-sand">Format</span>
                                        <span className="font-bold text-cream">{tr("Zoom — 30 min · 25 $", "Zoom — 30 min · $25")}</span>
                                    </div>
                                    <div className="flex justify-between border-b border-white/10 pb-3">
                                        <span className="text-sand">{tr("Présentiel", "In person")}</span>
                                        <span className="font-bold text-cream">Gatineau & Ottawa</span>
                                    </div>
                                </div>
                                {mailHref ? (
                                    <a
                                        href={mailHref}
                                        data-testid="booking-confirm-button"
                                        className="mt-8 w-full inline-flex items-center justify-center gap-3 rounded-full bg-terracotta px-8 py-4 text-sm font-bold uppercase tracking-wider text-white transition-colors duration-300 hover:bg-terracotta-hover"
                                    >
                                        <Mail className="h-4 w-4" /> {tr("Confirmer par courriel", "Confirm by email")}
                                    </a>
                                ) : (
                                    <p className="mt-8 text-center text-sm text-sand" data-testid="booking-hint">
                                        {tr("Choisis une date et un créneau pour confirmer ta réservation.", "Pick a date and a time to confirm your booking.")}
                                    </p>
                                )}
                                <p className="mt-5 text-xs text-sand leading-relaxed text-center">
                                    {tr(
                                        `Ta demande est envoyée à ${EMAIL}. Je te réponds sous 24 h avec le lien Zoom.`,
                                        `Your request is sent to ${EMAIL}. I reply within 24 hours with the Zoom link.`
                                    )}
                                </p>

                                <div className="mt-8 pt-8 border-t border-white/10 text-center">
                                    <p className="text-sm text-sand">{tr("Tu préfères parler tout de suite ?", "Prefer to talk right now?")}</p>
                                    <a
                                        href={waLink(tr(WA_DEFAULT_MSG))}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="contact-whatsapp-cta"
                                        className="mt-4 w-full inline-flex items-center justify-center gap-3 rounded-full bg-whatsapp px-8 py-4 text-sm font-bold uppercase tracking-wider text-white transition-opacity duration-300 hover:opacity-90"
                                    >
                                        <MessageCircle className="h-4 w-4" /> WhatsApp
                                    </a>
                                </div>
                            </div>
                        </Reveal>
                    </div>

                    <Reveal className="mt-16">
                        <div className="border border-white/10 bg-obsidian-surface p-6 md:p-10" data-testid="nutrition-form-section">
                            <Overline>{tr("Pas de WhatsApp ? Aucun problème", "No WhatsApp? No problem")}</Overline>
                            <h2 className="mt-3 font-heading text-3xl md:text-4xl font-bold tracking-tight">
                                {tr("Laisse-moi tes infos,", "Leave your info,")}{" "}
                                <span className="font-accent italic font-medium text-ochre">{tr("je te contacte.", "I'll contact you.")}</span>
                            </h2>
                            <p className="mt-4 text-sand font-light max-w-2xl">
                                {tr(
                                    "Remplis ce court questionnaire nutrition : tes coordonnées, ton objectif et tes habitudes alimentaires. Je te rappelle personnellement pour en discuter.",
                                    "Fill out this short nutrition questionnaire: your contact details, your goal and your eating habits. I'll call you back personally to discuss."
                                )}
                            </p>
                            <div className="mt-8">
                                <NutritionForm />
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
};

export default Contact;
